import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "../../../../config/api";
import { generateQrReportHtml } from "../utils/generateQrReportHtml";

function MeatSkewer({ width = 80, height = 90 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 80 90" fill="none">
      <g transform="rotate(-28 40 45)">
        <rect x="4" y="43" width="66" height="4" rx="2" fill="#D9A25C"/>
        <path d="M70 39 L80 45 L70 51 Z" fill="#D9A25C"/>
        <rect x="9" y="35" width="19" height="19" rx="5" fill="#7A3210"/>
        <rect x="31" y="35" width="19" height="19" rx="5" fill="#8B3A1A"/>
        <rect x="53" y="35" width="19" height="19" rx="5" fill="#7A3210"/>
        <ellipse cx="14" cy="40" rx="5" ry="4" fill="#fff" opacity="0.18"/>
        <ellipse cx="36" cy="40" rx="5" ry="4" fill="#fff" opacity="0.18"/>
        <ellipse cx="58" cy="40" rx="5" ry="4" fill="#fff" opacity="0.18"/>
        <path d="M13 40 L24 51 M22 39 L13 48" stroke="#4A1E0A" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M35 40 L46 51 M44 39 L35 48" stroke="#4A1E0A" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M57 40 L68 51 M66 39 L57 48" stroke="#4A1E0A" strokeWidth="1.6" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

const STATUS = { IDLE: "idle", LOADING: "loading", VALID: "valid", INVALID: "invalid", COMPLETED: "completed", ERROR: "error" };

function SectionValidate() {
  const [cameraActive, setCameraActive] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [status, setStatus] = useState(STATUS.IDLE);
  const [resultMessage, setResultMessage] = useState("");
  const [resultData, setResultData] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportPassword, setReportPassword] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const scannerRef = useRef(null);
  const isScanningRef = useRef(false);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const validate = async (code) => {
    if (!code.trim() || isScanningRef.current) return;
    isScanningRef.current = true;
    setStatus(STATUS.LOADING);
    setResultData(null);

    try {
      const res = await api.post("/validateQrCode", { ticket: code.trim() });

      // O interceptor do api.js retorna o erro em vez de lançar,
      // então dados de erro ficam em res.response?.data
      const body   = res.data ?? res.response?.data;
      const inner  = body?.data ?? body;
      const httpOk = res.status === 200 || res.response?.status === 200 || body?.status === 200;

      const msg = body?.message || inner?.message || "";

      if (httpOk) {
        setStatus(STATUS.VALID);
        setResultMessage(msg || "QR Code válido!");
        setResultData(inner);
      } else if (msg.toLowerCase().includes("já foi resgatado")) {
        setStatus(STATUS.COMPLETED);
        setResultMessage(msg);
      } else {
        setStatus(STATUS.INVALID);
        setResultMessage(msg || "QR Code inválido ou já utilizado. ❌");
      }
    } catch (err) {
      console.error(err);
      setStatus(STATUS.ERROR);
      setResultMessage("Erro ao validar. Verifique a conexão e tente novamente.");
    } finally {
      isScanningRef.current = false;
      stopCamera();
    }
  };

  const startCamera = async () => {
    setStatus(STATUS.IDLE);
    setResultData(null);
    setCameraActive(true);

    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            validate(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.error("Erro ao iniciar câmera:", err);
        setStatus(STATUS.ERROR);
        setResultMessage("Não foi possível acessar a câmera. Verifique as permissões.");
        setCameraActive(false);
      }
    }, 100);
  };

  const stopCamera = () => {
    if (scannerRef.current) {
      scannerRef.current
        .stop()
        .then(() => {
          scannerRef.current.clear();
          scannerRef.current = null;
        })
        .catch(() => {});
    }
    setCameraActive(false);
  };

  const handleToggleCamera = () => {
    if (cameraActive) {
      stopCamera();
    } else {
      startCamera();
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    validate(manualCode);
  };

  const handleReset = () => {
    setStatus(STATUS.IDLE);
    setResultData(null);
    setResultMessage("");
    setManualCode("");
    isScanningRef.current = false;
  };

  const handleCloseReportModal = () => {
    if (reportLoading) return;
    setShowReportModal(false);
    setReportPassword("");
    setReportError("");
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    if (!reportPassword.trim() || reportLoading) return;

    setReportLoading(true);
    setReportError("");

    try {
      const res = await api.post("/qrCodeReport", { password: reportPassword });

      const body = res.data ?? res.response?.data;
      const httpOk = res.status === 200 || res.response?.status === 200 || body?.status === 200;

      if (!httpOk) {
        setReportError(body?.message || "Não foi possível gerar o relatório.");
        return;
      }

      const html = generateQrReportHtml(body.data || []);
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `relatorio-agrega+gh${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setShowReportModal(false);
      setReportPassword("");
    } catch (err) {
      console.error("Erro ao gerar relatório:", err);
      setReportError("Erro ao gerar relatório. Tente novamente.");
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="sectionValidate">
      <div className="validateContainer">
        <div className="validateHeader">
          <h1>Validação de QR Code</h1>
          <p>Agrega + GH — Use a câmera ou digite o código do QR Code</p>
        </div>

        <div className="reportButtonWrapper">
          <button
            type="button"
            className="reportBtn"
            onClick={() => setShowReportModal(true)}
          >
            📊 Gerar relatório
          </button>
        </div>

        {showReportModal && (
          <div className="reportModalOverlay" onClick={handleCloseReportModal}>
            <form
              className="reportModal"
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleGenerateReport}
            >
              <h3>Gerar relatório</h3>
              <p>Digite a senha de acesso para gerar o relatório dos cadastros.</p>
              <input
                type="password"
                value={reportPassword}
                onChange={(e) => setReportPassword(e.target.value)}
                placeholder="Senha de acesso"
                autoFocus
              />
              {reportError && <p className="reportModalError">{reportError}</p>}
              <div className="reportModalActions">
                <button
                  type="button"
                  className="reportModalCancel"
                  onClick={handleCloseReportModal}
                  disabled={reportLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="reportModalSubmit"
                  disabled={reportLoading || !reportPassword.trim()}
                >
                  {reportLoading ? "Gerando..." : "Gerar"}
                </button>
              </div>
            </form>
          </div>
        )}

        {status === STATUS.IDLE || status === STATUS.ERROR ? (
          <div className="validateCard">
            {/* Camera */}
            <div className="cameraSection">
              <button
                className={`cameraBtn ${cameraActive ? "active" : ""}`}
                onClick={handleToggleCamera}
              >
                <span className="cameraIcon">
                  {cameraActive ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
                      <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </span>
                {cameraActive ? "Parar câmera" : "Abrir câmera"}
              </button>

              <div
                id="qr-reader"
                className={`qrReader ${cameraActive ? "visible" : ""}`}
              />
            </div>

            <div className="divider">
              <span>ou</span>
            </div>

            {/* Manual input */}
            <form className="manualForm" onSubmit={handleManualSubmit}>
              <label>Código do QR Code</label>
              <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Cole ou digite o código aqui"
                autoComplete="off"
              />
              <button
                type="submit"
                className="validateBtn"
                disabled={!manualCode.trim()}
              >
                Validar código
              </button>
            </form>

            {status === STATUS.ERROR && (
              <p className="errorMsg">{resultMessage}</p>
            )}
          </div>
        ) : status === STATUS.LOADING ? (
          <div className="validateCard resultCard">
            <div className="spinner" />
            <p>Validando...</p>
          </div>
        ) : (
          <div className={`validateCard resultCard ${status}`}>
            <div className="resultIcon">
              {status === STATUS.VALID && (
                <svg viewBox="0 0 24 24" fill="none" width="64" height="64">
                  <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2"/>
                  <path d="M8 12L11 15L16 9" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {status === STATUS.COMPLETED && (
                <MeatSkewer width={56} height={63} />
              )}
              {status === STATUS.INVALID && (
                <svg viewBox="0 0 24 24" fill="none" width="64" height="64">
                  <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                  <path d="M15 9L9 15M9 9L15 15" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              )}
            </div>

            <h2 className="resultTitle">{resultMessage}</h2>

            {status === STATUS.VALID && resultData && (
              <div className="resultInfo">
                <div className="choppBadge">
                  <MeatSkewer width={22} height={25} /> Vale-churrasco resgatado
                </div>
                {resultData.name && <p><strong>Nome:</strong> {resultData.name}</p>}
                {resultData.phone && <p><strong>Telefone:</strong> {resultData.phone}</p>}
                {resultData.vehicleType && <p><strong>Veículo e conjunto:</strong> {resultData.vehicleType}</p>}
                {resultData.permission && <p><strong>Permisso:</strong> {resultData.permission}</p>}
              </div>
            )}

            <button className="resetBtn" onClick={handleReset}>
              Validar outro ticket
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SectionValidate;
